import React, { useState, useEffect, useContext } from 'react';
import ReactECharts from 'echarts-for-react';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import { AuthContext } from '../../context/AuthContext';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState({
    totalUsers: 0, totalInvitations: 0, accepted: 0, declined: 0,
    pending: 0, newUsersToday: 0, newUsersThisWeek: 0,
    recentUsers: [], recentInvitations: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const [
        { count: totalUsers }, { count: totalInvitations },
        { count: accepted }, { count: declined }, { count: pending },
        { data: recentUsers }, { data: recentInvitations }
      ] = await Promise.all([
        supabase.from('user_profiles').select('*', { count: 'exact', head: true }).neq('role', 'admin'),
        supabase.from('invitations').select('*', { count: 'exact', head: true }),
        supabase.from('invitations').select('*', { count: 'exact', head: true }).eq('status', 'accepted'),
        supabase.from('invitations').select('*', { count: 'exact', head: true }).eq('status', 'declined'),
        supabase.from('invitations').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('user_profiles').select('id, name, email, created_at, avatar_url').neq('role', 'admin').order('created_at', { ascending: false }).limit(8),
        supabase.from('invitations').select('id, recipient_name, occasion, status, created_at, category').order('created_at', { ascending: false }).limit(8)
      ]);

      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString();
      const [{ count: newUsersToday }, { count: newUsersThisWeek }] = await Promise.all([
        supabase.from('user_profiles').select('*', { count: 'exact', head: true }).neq('role', 'admin').gte('created_at', today),
        supabase.from('user_profiles').select('*', { count: 'exact', head: true }).neq('role', 'admin').gte('created_at', weekAgo)
      ]);

      setStats({
        totalUsers: totalUsers || 0, totalInvitations: totalInvitations || 0,
        accepted: accepted || 0, declined: declined || 0, pending: pending || 0,
        newUsersToday: newUsersToday || 0, newUsersThisWeek: newUsersThisWeek || 0,
        recentUsers: recentUsers || [], recentInvitations: recentInvitations || []
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const responseRate = stats.totalInvitations > 0
    ? Math.round(((stats.accepted + stats.declined) / stats.totalInvitations) * 100) : 0;
  const acceptanceRate = (stats.accepted + stats.declined) > 0
    ? Math.round((stats.accepted / (stats.accepted + stats.declined)) * 100) : 0;

  const statCards = [
    { label: 'Total Users', value: stats.totalUsers, icon: 'Users', gradient: 'from-blue-500 to-blue-600', sub: `+${stats.newUsersToday} today`, sub2: `+${stats.newUsersThisWeek} this week` },
    { label: 'Total Invitations', value: stats.totalInvitations, icon: 'Mail', gradient: 'from-pink-500 to-rose-500', sub: `${stats.pending} pending`, sub2: `${stats.accepted} accepted` },
    { label: 'Acceptance Rate', value: `${acceptanceRate}%`, icon: 'Heart', gradient: 'from-emerald-500 to-emerald-600', sub: `${stats.accepted} accepted`, sub2: `${stats.declined} declined` },
    { label: 'Response Rate', value: `${responseRate}%`, icon: 'MessageCircle', gradient: 'from-purple-500 to-purple-600', sub: `${stats.accepted + stats.declined} responded`, sub2: `${stats.pending} awaiting` },
  ];

  const invitationChartOption = {
    tooltip: { trigger: 'item', backgroundColor: '#fff', borderColor: '#e5e7eb', textStyle: { color: '#374151' } },
    legend: { bottom: '0%', left: 'center', textStyle: { color: '#6b7280' } },
    series: [{
      type: 'pie', radius: ['45%', '75%'], avoidLabelOverlap: false,
      itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 3 },
      label: { show: false },
      emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold' } },
      data: [
        { value: stats.accepted, name: 'Accepted', itemStyle: { color: '#10b981' } },
        { value: stats.declined, name: 'Declined', itemStyle: { color: '#ef4444' } },
        { value: stats.pending, name: 'Pending', itemStyle: { color: '#f59e0b' } },
      ]
    }]
  };

  const userGrowthOption = {
    tooltip: { trigger: 'axis', backgroundColor: '#fff', borderColor: '#e5e7eb', textStyle: { color: '#374151' } },
    grid: { left: '8%', right: '4%', top: '8%', bottom: '12%' },
    xAxis: {
      type: 'category', data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      axisLine: { lineStyle: { color: '#e5e7eb' } }, axisLabel: { color: '#6b7280' }
    },
    yAxis: {
      type: 'value', axisLine: { show: false },
      splitLine: { lineStyle: { color: '#f3f4f6' } }, axisLabel: { color: '#6b7280' }
    },
    series: [{
      data: [2, 5, 3, 8, 4, 6, stats.newUsersThisWeek], type: 'bar', barWidth: '45%',
      itemStyle: {
        borderRadius: [8, 8, 0, 0],
        color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [{ offset: 0, color: '#ec4899' }, { offset: 1, color: '#f43f5e' }]
        }
      }
    }]
  };

  const formatDate = (date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-pink-500 via-rose-500 to-red-500 rounded-2xl p-8 text-white">
        <h2 className="text-2xl font-bold mb-2">Welcome back, {user?.name || 'Admin'} 👋</h2>
        <p className="text-pink-100 text-sm">Here's what's happening with DayClap today.</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-5">
              <div className={`w-14 h-14 bg-gradient-to-br ${card.gradient} rounded-2xl flex items-center justify-center shadow-lg`}>
                <SafeIcon name={card.icon} className="text-white text-2xl" />
              </div>
            </div>
            <p className="text-4xl font-bold text-gray-900">{card.value}</p>
            <p className="text-sm font-medium text-gray-500 mt-1">{card.label}</p>
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">{card.sub}</span>
              <span className="text-xs text-gray-300">·</span>
              <span className="text-xs text-gray-400">{card.sub2}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-gray-800 text-lg">Invitation Responses</h3>
            <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">All time</span>
          </div>
          <ReactECharts option={invitationChartOption} style={{ height: 300 }} />
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-gray-800 text-lg">User Growth</h3>
            <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">This week</span>
          </div>
          <ReactECharts option={userGrowthOption} style={{ height: 300 }} />
        </div>
      </div>

      {/* Recent Activity Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-8 py-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-gray-800 text-lg">Recent Users</h3>
            <span className="text-xs text-gray-400">{stats.recentUsers.length} shown</span>
          </div>
          <div className="divide-y divide-gray-50">
            {stats.recentUsers.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-12">No users yet</p>
            ) : (
              stats.recentUsers.map((u) => (
                <div key={u.id} className="flex items-center gap-4 px-8 py-4 hover:bg-gray-50 transition-colors">
                  <img
                    src={u.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${u.email}`}
                    alt={u.name}
                    className="w-11 h-11 rounded-full ring-2 ring-gray-100"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{u.name || 'Unnamed'}</p>
                    <p className="text-xs text-gray-400 truncate">{u.email}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(u.created_at)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Invitations */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-8 py-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-gray-800 text-lg">Recent Invitations</h3>
            <span className="text-xs text-gray-400">{stats.recentInvitations.length} shown</span>
          </div>
          <div className="divide-y divide-gray-50">
            {stats.recentInvitations.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-12">No invitations yet</p>
            ) : (
              stats.recentInvitations.map((inv) => (
                <div key={inv.id} className="flex items-center gap-4 px-8 py-4 hover:bg-gray-50 transition-colors">
                  <div className="w-11 h-11 bg-pink-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon name="Heart" className="text-pink-500 text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">To {inv.recipient_name}</p>
                    <p className="text-xs text-gray-400 truncate">{inv.occasion} · {inv.category}</p>
                  </div>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full flex-shrink-0 ${
                    inv.status === 'accepted' ? 'bg-green-100 text-green-700' :
                    inv.status === 'declined' ? 'bg-red-100 text-red-700' :
                    inv.status === 'cancelled' ? 'bg-gray-100 text-gray-500' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {inv.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;